import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Dialog, Typography, Grid, Button, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RegEx } from 'utils';
import { UserOrgEdit } from '.';
import { Divider } from '@material-ui/core';

const UserInviteForm = ({ open, onClose, onInvite, selectedOrganization, isSuper }) => {
  const { t } = useTranslation();

  const [inviteData, setInviteData] = useState({
    email: '',
    confirm_email: '',
    organization: undefined,
  });

  const [inviteErrors, setInviteErrors] = useState({
    email: '',
    confirm_email: '',
    organization: '',
  });

  const setError = (field, message) => {
    setInviteErrors({
      ...inviteErrors,
      [field]: message,
    });
  };

  const setData = (field, value) => {
    setInviteData({
      ...inviteData,
      [field]: value,
    });
  };

  const handleInvite = () => {
    onInvite(inviteData?.email.toLowerCase(), isSuper ? inviteData?.organization : selectedOrganization);
    setError('email', '');
    setError('confirm_email', '');
    setError('organization', '');
    onClose();
  };

  const setInvitedUserOrg = (organization) => {
    if (inviteErrors.organization !== '') {
      setError('organization', '');
    }
    setData('organization', organization);
  };

  const emailsMatch = (field, realTimeValue) => {
    if (field === 'confirm_email' && realTimeValue !== inviteData?.email) {
      setError(field, t('emails_dont_match'));
    } else if (field === 'email' && inviteData?.confirm_email !== '' && realTimeValue !== inviteData?.confirm_email) {
      setError(field, t('emails_dont_match'));
    } else {
      setError(field, '');
    }
  };

  const lostFocus = (event, field) => {
    const realTimeValue = event.target.value;
    emailsMatch(field, realTimeValue);
    if (realTimeValue.trim() === '') {
      setError(field, t('required'));
    } else if (!RegEx.isEmail(realTimeValue)) {
      setError(field, t('invalid_email'));
    }
  };

  const formIsValid = () => {
    const hasOrganization = !isSuper || (!!inviteData.organization && inviteErrors.organization === '');
    return (
      hasOrganization &&
      RegEx.isEmail(inviteData.email) &&
      inviteErrors.email === '' &&
      RegEx.isEmail(inviteData.confirm_email) &&
      inviteErrors.confirm_email === ''
    );
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <Grid container direction="column" justifyContent="center" spacing={2} p={2}>
        <Grid item>
          <Typography variant="h6">{t('invite_user')}</Typography>
        </Grid>
        <Grid item>
          <Typography variant="body">{t('invite_user_instructions')}</Typography>
        </Grid>
        <Grid item>
          <TextField
            onBlur={(event) => {
              lostFocus(event, 'email');
            }}
            label={t('email')}
            value={inviteData?.email}
            onChange={(e) => {
              if (inviteErrors?.email) {
                setError('email', '');
              }
              setData('email', e.target.value);
            }}
            variant="outlined"
            fullWidth
            error={inviteErrors.email !== ''}
            helperText={inviteErrors.email}
          />
        </Grid>
        <Grid item>
          <TextField
            onBlur={(event) => {
              lostFocus(event, 'confirm_email');
            }}
            label={t('confirm_email')}
            value={inviteData?.confirm_email}
            disabled={!inviteData?.email || inviteData?.emailError}
            onChange={(e) => {
              if (inviteErrors?.confirm_email) {
                setError('confirm_email', '');
              }
              setData('confirm_email', e.target.value);
            }}
            variant="outlined"
            fullWidth
            error={inviteErrors?.confirm_email !== ''}
            helperText={inviteErrors?.confirm_email}
          />
        </Grid>
        {isSuper && (
          <Grid item>
            <UserOrgEdit
              desiredOrg={inviteData.organization}
              setUserOrg={setInvitedUserOrg}
              organizationError={inviteErrors?.organization !== ''}
              organizationErrorMessage={inviteErrors?.organization}
            />
          </Grid>
        )}
        <Grid item>
          <Divider />
        </Grid>
        <Grid item>
          <Button variant="contained" color="primary" onClick={handleInvite} disabled={!formIsValid()}>
            {t('invite')}
          </Button>
        </Grid>
      </Grid>
    </Dialog>
  );
};

UserInviteForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onInvite: PropTypes.func.isRequired,
  selectedOrganization: PropTypes.object,
  isSuper: PropTypes.bool,
};

UserInviteForm.desiredOrg = {
  selectedOrganization: undefined,
  isSuper: false,
};

export default UserInviteForm;
